/**
 * Utilities for stabilizing Jotai derived atom references.
 *
 * Jotai uses Object.is for atom value equality. Derived atoms that return
 * new Set/Map/Array objects on every evaluation cause unnecessary re-renders
 * even when the *contents* haven't changed. These helpers cache the previous
 * value and return the same reference when contents are equal.
 */

/** Shallow content-equality check for two Sets. */
function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a.size !== b.size) return false;
  for (const item of a) {
    if (!b.has(item)) return false;
  }
  return true;
}

/**
 * Returns a stabilizer function that caches the last Set value.
 * If the new Set has the same contents as the cached one, the cached
 * reference is returned — preventing downstream re-renders.
 *
 * Usage:
 *   const stabilize = createStableSet<string>();
 *   export const myAtom = atom((get) => stabilize(new Set(...)));
 */
export function createStableSet<T>(): (next: Set<T>) => Set<T> {
  let prev: Set<T> = new Set();
  return (next: Set<T>): Set<T> => {
    if (setsEqual(prev, next)) return prev;
    prev = next;
    return next;
  };
}
